import React, { useMemo } from "react";
import Sparkline from "./shared/Sparkline";
// import { CHART_COLORS } from "../constants/analyticsConstants";
import { transformEventAnalytics, fmtTime, fmtEventName, colorForCode } from "../utils/analyticsUtils";

/* ── H-Bar (local, uses CSS classes) ─────────────────────────────────────── */
function HBar({ label, value, max, color, sub, delay = 0 }) {
  const pct = Math.round((value / Math.max(max, 1)) * 100);
  return (
    <div className="ua-hbar">
      <div className="ua-hbar-row">
        <span className="ua-hbar-label">{label}</span>
        <span className="ua-hbar-val">
          {value.toLocaleString()}
          {sub && <span style={{ opacity: .55, marginLeft: 4 }}>· {sub}</span>}
        </span>
      </div>
      <div className="ua-hbar-track">
        <div
          className="ua-hbar-fill"
          style={{ width: `${pct}%`, background: color, animationDelay: `${delay}s` }}
        />
      </div>
    </div>
  );
}

/* ── Card wrapper ─────────────────────────────────────────────────────────── */
function Card({ title, sub, accent = "p", delay = 0, children }) {
  return (
    <div className={`ua-card ${accent}`} style={{ animationDelay: `${delay}s` }}>
      <div className="ua-card-head">
        <span className="ua-card-title">{title}</span>
        {sub && <span className="ua-card-sub">{sub}</span>}
      </div>
      {children}
    </div>
  );
}

/* ── Event log row ────────────────────────────────────────────────────────── */
function EventRow({ ev }) {
  const color = colorForCode(ev.event_code);
  const srcColors = { user:"#3B82F6", device:"#10B981", system:"#6366F1" };
  const srcBg    = { user:"#EFF6FF",  device:"#ECFDF5",  system:"#EEF2FF" };
  const src = ev.source ?? "system";

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "180px 1fr 120px 80px 70px 60px",
      alignItems: "center", gap: 12,
      padding: "10px 14px", borderRadius: 10,
      transition: "background .15s", cursor: "default",
      fontSize: 13,
    }}
    onMouseEnter={e => e.currentTarget.style.background = "#F8F7FF"}
    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >
      {/* Event name badge */}
      <span style={{
        fontSize: 11, fontWeight: 700,
        padding: "3px 10px", borderRadius: 999,
        background: color + "18", color,
        border: `1px solid ${color}33`,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {fmtEventName(ev.event_name)}
      </span>

      {/* Screen */}
      <span style={{ color: "#5B5A8A", fontSize: 12, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
        {ev.screen ?? "—"}
      </span>

      {/* Time */}
      <span style={{ color: "#9B99C4", fontFamily: "monospace", fontSize: 11 }}>
        {fmtTime(ev.timestamp)}
      </span>

      {/* Source */}
      <span style={{
        fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
        background: srcBg[src] ?? "#F1F5F9",
        color: srcColors[src] ?? "#475569",
        textAlign: "center",
      }}>
        {src}
      </span>

      {/* Platform */}
      <span style={{ color: "#9B99C4", fontSize: 12, textTransform: "capitalize" }}>
        {ev.device_info?.platform ?? "—"}
      </span>

      {/* Code */}
      <span style={{
        fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 4,
        background: "#EEF2FF", color: "#4F46E5",
        border: "1px solid rgba(99,102,241,.15)",
        fontFamily: "monospace", textAlign: "center",
      }}>
        {ev.event_code}
      </span>
    </div>
  );
}

/* ── Main ─────────────────────────────────────────────────────────────────── */
export default function EventsTab({ data }) {
  const eventDist = useMemo(() => transformEventAnalytics(data?.event_analytics), [data]);
  const maxCount  = Math.max(...eventDist.map(e => e.count), 1);
  const items     = data?.events?.items ?? [];
  const total     = eventDist.reduce((s, e) => s + e.count, 0);

  return (
    <div className="ua-vstack">

      {/* Distribution + sparklines side by side */}
      <div className="ua-grid-1-1">
        <Card title="Event distribution" sub={`${total} total`} accent="p" delay={0.05}>
          {eventDist.length === 0
            ? <p style={{ color:"#9B99C4", fontSize:13 }}>No event data for this range.</p>
            : eventDist.map((e, i) => (
                <HBar key={e.name} label={e.displayName} value={e.count}
                  max={maxCount} color={e.color} sub={e.name} delay={i * .06} />
              ))
          }
        </Card>

        <Card title="Event trends" sub="daily counts" accent="b" delay={0.10}>
          <div className="ua-grid-3" style={{ gap: 12 }}>
            {eventDist.length === 0
              ? <p style={{ color:"#9B99C4", fontSize:13, gridColumn:"1/-1" }}>No trend data.</p>
              : eventDist.map((e) => (
                  <div key={e.name} className="ua-spark-card">
                    <div className="ua-spark-name">{e.displayName}</div>
                    <div className="ua-spark-value">{e.count.toLocaleString()}</div>
                    {e.series?.length > 1
                      ? <Sparkline data={e.series.map(d => ({ value: d.count, date: d.date }))} color={e.color} height={32} />
                      : <div style={{ height: 32, display:"flex", alignItems:"center" }}>
                          <div style={{ height: 2, width:"100%", background: e.color, opacity:.35, borderRadius: 2 }} />
                        </div>
                    }
                  </div>
                ))
            }
          </div>
        </Card>
      </div>

      {/* Raw event log */}
      <Card title="Event log" sub={`${items.length} events · page 1`} accent="n" delay={0.15}>
        {/* Table header */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "180px 1fr 120px 80px 70px 60px",
          gap: 12, padding: "8px 14px",
          background: "#F8F7FF", borderRadius: 10, marginBottom: 6,
          fontSize: 10, fontWeight: 800, color: "#9B99C4",
          letterSpacing: ".08em", textTransform: "uppercase",
        }}>
          <span>Event</span>
          <span>Screen</span>
          <span>Time (IST)</span>
          <span>Source</span>
          <span>Platform</span>
          <span>Code</span>
        </div>

        {items.length === 0
          ? <p style={{ color:"#9B99C4", fontSize:13, padding:"16px 14px" }}>No events found.</p>
          : items.map(ev => <EventRow key={ev._id} ev={ev} />)
        }

        {data?.events?.has_more && (
          <div style={{ textAlign:"center", padding:"12px 0 4px", fontSize:13, color:"#6366F1", fontWeight:600 }}>
            More events available — adjust limit or use pagination
          </div>
        )}
      </Card>
    </div>
  );
}